<script lang="ts">
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import Check from '@lucide/svelte/icons/check';
	import ChevronDown from '@lucide/svelte/icons/chevron-down';

	type Layout = 'inline' | 'menu';

	interface Props {
		layout?: Layout;
		compact?: boolean;
		class?: string;
		onSelect?: () => void;
	}

	const displayLocales = ['es', 'en', 'gl'] as const;

	const localeLabels: Record<(typeof displayLocales)[number], string> = {
		es: 'Español',
		en: 'English',
		gl: 'Galego'
	};

	let { layout = 'inline', compact = false, class: className = '', onSelect }: Props = $props();

	const currentLocale = $derived(getLocale());

	function shortLabel(locale: (typeof displayLocales)[number]) {
		return locale.toUpperCase();
	}

	function fullLabel(locale: (typeof displayLocales)[number]) {
		return localeLabels[locale];
	}

	const currentLabel = $derived(shortLabel(currentLocale as (typeof displayLocales)[number]));

	const contentClass = $derived(
		layout === 'menu'
			? 'z-75 min-w-36 rounded-lg border border-zp-line bg-zp-paper p-1 shadow-[0_12px_28px_-16px_rgba(27,24,64,0.35)]'
			: 'z-75 min-w-36 rounded-lg border border-zp-line bg-zp-paper p-1 shadow-[0_12px_28px_-16px_rgba(27,24,64,0.35)]'
	);

	let open = $state(false);

	const wrapperClass = $derived(
		layout === 'menu'
			? `shrink-0 border-t border-zp-line pt-3 ${className}`
			: `hidden md:block ${className}`
	);

	const triggerClass = $derived(
		layout === 'menu'
			? 'inline-flex w-full min-h-10 items-center justify-between gap-2 rounded-[9px] border border-zp-line bg-zp-paper2 px-3 py-2 text-[0.82rem] font-semibold tracking-[0.01em] text-zp-ink transition-colors hover:bg-zp-paper data-[state=open]:border-zp-ink/25'
			: compact
				? 'inline-flex items-center gap-0.5 rounded-md border border-zp-line/80 bg-transparent px-2 py-1 text-[0.78rem] font-semibold tracking-[0.01em] text-zp-ink/78 transition-colors hover:border-zp-line hover:text-zp-ink data-[state=open]:border-zp-line data-[state=open]:text-zp-ink md:h-6 md:px-2 md:py-0 md:text-[0.7rem] md:leading-none'
				: 'inline-flex items-center gap-1 rounded-md border border-zp-line/80 bg-transparent px-2.5 py-2 text-[0.78rem] font-semibold tracking-[0.01em] text-zp-ink/78 transition-colors hover:border-zp-line hover:text-zp-ink data-[state=open]:border-zp-line data-[state=open]:text-zp-ink md:px-3 md:py-2 md:text-[0.83rem]'
	);
</script>

<div class={wrapperClass}>
	<DropdownMenu.Root bind:open={open}>
		<DropdownMenu.Trigger class={triggerClass} aria-label="Idioma">
			<span>{currentLabel}</span>
			<ChevronDown
				class="shrink-0 opacity-70 transition-transform duration-200 {compact
					? 'size-3 md:size-2.5'
					: 'size-3.5'} {open ? 'rotate-180' : ''}"
			/>
		</DropdownMenu.Trigger>
		<DropdownMenu.Content
			align={layout === 'menu' ? 'center' : 'end'}
			sideOffset={6}
			class={contentClass}
		>
			{#each displayLocales as locale (locale)}
				{#if locale === currentLocale}
					<DropdownMenu.Item
						class="flex cursor-default items-center justify-between gap-3 rounded-md px-2.5 py-2 text-sm font-semibold text-zp-ink focus:bg-zp-paper2"
						onSelect={(e) => e.preventDefault()}
					>
						{fullLabel(locale)}
						<Check class="size-3.5 text-zp-accent" aria-hidden="true" />
					</DropdownMenu.Item>
				{:else}
					<DropdownMenu.Item class="p-0 focus:bg-transparent">
						<a
							href={resolve(localizeHref(page.url.pathname, { locale }))}
							data-sveltekit-reload
							class="flex w-full items-center rounded-md px-2.5 py-2 text-sm font-medium text-zp-ink/78 outline-none hover:bg-zp-paper2 hover:text-zp-ink"
							onclick={() => {
								open = false;
								onSelect?.();
							}}
						>
							{fullLabel(locale)}
						</a>
					</DropdownMenu.Item>
				{/if}
			{/each}
		</DropdownMenu.Content>
	</DropdownMenu.Root>
</div>
